import React from 'react';
import { Trash2 } from 'lucide-react';
import { deleteConfiguracionPago, ConfiguracionPago } from '../../services/configuracionPagoService';

interface DeleteConfigPagosProps {
    isOpen: boolean;
    onClose: () => void;
    onConfiguracionDeleted: (id: number) => void;
    configuracion: ConfiguracionPago | null;
}

const DeleteConfigPagos: React.FC<DeleteConfigPagosProps> = ({ 
    isOpen, 
    onClose, 
    onConfiguracionDeleted,
    configuracion 
}) => {
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const handleDelete = async () => {
        if (!configuracion) return;
        
        try {
            setIsDeleting(true);
            setError(null);
            
            await deleteConfiguracionPago(configuracion.id);
            
            // Cerrar modal y pasar el ID eliminado al componente padre
            onConfiguracionDeleted(configuracion.id);
            onClose();
        } catch (err) {
            setError('Error al eliminar la configuración de pago. Por favor intente nuevamente.');
            /* Browser diagnostics omitted. */
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen || !configuracion) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            <div className="bg-white p-8 rounded-2xl w-full max-w-md border border-gray-100 shadow-2xl relative">
                <div className="flex justify-between items-start mb-6">
                    <h3 className="text-[20px] font-bold text-gray-900">Confirmar eliminación</h3>
                    <button 
                        onClick={onClose} 
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {error && (
                    <div className="mb-6 p-3 bg-red-50 text-red-600 rounded-md border border-red-100">
                        {error}
                    </div>
                )}

                <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 gap-3">
                        <div>
                            <span className="text-sm font-medium text-gray-500">Nombre:</span>
                            <p className="text-gray-900 font-medium">{configuracion.nombre}</p>
                        </div>
                        <div>
                            <span className="text-sm font-medium text-gray-500">Tipo:</span>
                            <p className="text-gray-900">
                                {configuracion.tipo === 'efectivo' && 'Efectivo'}
                                {configuracion.tipo === 'banco' && 'Banco'}
                                {configuracion.tipo === 'otro' && 'Otro'}
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <span className="text-sm font-medium text-gray-500">Afecta caja:</span>
                                <p className="text-gray-900">{configuracion.afecta_caja ? 'Sí' : 'No'}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-500">Requiere cambio:</span>
                                <p className="text-gray-900">{configuracion.requiere_cambio ? 'Sí' : 'No'}</p>
                            </div>
                        </div>
                        <div>
                            <span className="text-sm font-medium text-gray-500">Estado:</span>
                            <p className="text-gray-900">
                                {configuracion.activo ? (
                                    <span className="inline-flex text-xs font-medium rounded-full bg-green-100 text-green-800 px-2 py-0.5">
                                        Activo
                                    </span>
                                ) : (
                                    <span className="inline-flex text-xs font-medium rounded-full bg-red-100 text-red-800 px-2 py-0.5">
                                        Inactivo
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                <p className="mb-6 text-gray-600">
                    ¿Estás seguro que deseas eliminar la configuración de pago{' '}
                    <strong className="text-red-600">"{configuracion.nombre}"</strong>?
                </p>

                <p className="text-sm text-gray-500 mb-6 text-center">
                    Esta acción no se puede deshacer. La configuración será eliminada permanentemente.
                </p>

                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={isDeleting}
                        className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 font-medium text-[14px]"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 font-medium shadow-sm flex items-center text-[14px]"
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {isDeleting ? 'Eliminando...' : 'Eliminar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeleteConfigPagos;